"use client";

import { motion } from "framer-motion";
import { PortableText } from "@portabletext/react";

import { GroqImage } from "@/helpers/Image";
import GradualBlur from "@/shadcn/GradualBlur";
import type { PagePatrickAffiliateSchema } from "@/sanity/pages/PagePatrickAffiliateTemplate";
import { measuredRichText } from "./richtext";
import ChevronCta from "./ChevronCta";

import waveDots from "@/public/fabio-affiliate/noise.jpg";

const ease = [0.25, 0.1, 0.25, 1] as const;

/**
 * "Lock in the world's most prop trading offer." — Figma 8841:2132 (desktop)
 * / 8811:3096 (mobile).
 *
 * The art below the button is the 16:9 cut-out shown through a 3:2 box
 * (772 × 515 / 408 × 272) at 118.73% width, then mirrored and tilted 3.5°.
 * Figma reports the crop as left -8.18% / top -5.34% in the un-mirrored
 * image's frame; inside a box that is itself mirrored that is left -10.55%
 * (100 - 118.73 + 8.18) and top 0 — fitted against the frame's render, the
 * silhouettes overlap 78% that way versus 62% with the reported values. Its rotated bounding box starts 72px / 81px above
 * the art area, i.e. the box itself sits at -48.8px / -68.5px, and the whole
 * thing dissolves into the footer through a blurred gradient band (15.5px /
 * 10.9px blur). The design also puts a faint white inner glow on the cut-out's
 * alpha edge, which CSS cannot draw on an image's alpha — left out.
 *
 * Mobile spacing is not the desktop's scaled: the art area starts 68px under
 * the button (24px on desktop) and is 205px tall (474px); the band is 123px
 * to the area's bottom (253px, ending 15px above it on desktop). The mobile
 * cut-out is centred on an 850px frame that sits 8px left of the viewport
 * centre, so it lands 3.8px left of centre; desktop 1px right.
 */
export default function FinalCTASection({ data }: { data: PagePatrickAffiliateSchema }) {
  if (
    !data?.finalCtaChip &&
    !data?.finalCtaHeading?.length &&
    !data?.finalCtaSubheading &&
    !data?.finalCtaButton &&
    !data?.finalCtaImage
  )
    return null;

  return (
    <section className="relative overflow-hidden bg-[#F4F4F4] pt-[6.84rem] lg:pt-[15rem]">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-10 mix-blend-exclusion"
        style={{ backgroundImage: `url(${waveDots.src})`, backgroundSize: "cover" }}
      />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.35 }}
        transition={{ duration: 0.8, ease }}
        className="relative mx-auto flex w-[min(106rem,calc(100%-4rem))] flex-col items-center gap-[2.5rem]">
        <div className="flex w-full flex-col items-center gap-[1.25rem]">
          {data?.finalCtaChip && (
            <p className="m-0 text-center font-blacker text-[1.875rem] leading-[1.5] text-black italic">
              {data.finalCtaChip}
            </p>
          )}
          {/* The heading is one block wrapping onto 2 / 3 lines, so the
              design's text gradient maps onto its box as drawn: black up to
              57.65% from the bottom, #595959 from 80.6% — grey at the top. The
              highlighted words keep their own colour and -0.03em tracking.
              `main` sets `text-wrap: balance` site-wide (main.scss), which on
              desktop pushed "most" down and split the highlighted line; the
              design breaks after it, and greedy wrapping gives exactly that.
              Mobile keeps `balance` — the heading runs to three lines there. */}
          {data?.finalCtaHeading?.length > 0 && (
            <div className="richtext richtext-center w-[40.25rem] [--hl-tracking:-0.03em] [--hl-dy:0.8rem] lg:[--hl-dy:-0.25rem] lg:w-full lg:[text-wrap:wrap] prose-headings:bg-gradient-to-t prose-headings:from-black prose-headings:from-[57.65%] prose-headings:to-[#595959] prose-headings:to-[80.6%] prose-headings:bg-clip-text prose-headings:font-geist prose-headings:font-[600] prose-headings:text-[5rem] prose-headings:leading-none prose-headings:tracking-[-0.25rem] prose-headings:text-transparent lg:prose-headings:text-[9rem] lg:prose-headings:tracking-[-0.45rem]">
              <PortableText
                value={data.finalCtaHeading}
                components={measuredRichText}
              />
            </div>
          )}
        </div>

        {data?.finalCtaSubheading && (
          <p className="m-0 w-[37.75rem] text-center font-geist text-[2.25rem] leading-[1.5] text-black/80 lg:w-auto">
            {data.finalCtaSubheading}
          </p>
        )}

        {data?.finalCtaButton && (
          <ChevronCta
            data={data.finalCtaButton}
            language={data?.settings?.language}
          />
        )}
      </motion.div>

      {data?.finalCtaImage && (
        <div className="relative mt-[8.5rem] h-[25.625rem] w-full lg:mt-[3rem] lg:h-[59.25rem]">
          {/* Positioning lives on this wrapper and nothing else transforms it:
              framer writes `transform` on the element it animates, which would
              throw away a Tailwind translate on the same element. */}
          <div className="absolute -top-[8.5625rem] left-[calc(50%-0.4725rem)] h-[33.95rem] w-[50.96rem] -translate-x-1/2 lg:-top-[6.1rem] lg:left-[calc(50%+0.125rem)] lg:h-[64.325rem] lg:w-[96.5rem]">
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1.4, ease, delay: 0.3 }}
              className="h-full w-full">
              {/* Figma flips the cut-out and tilts it 3.5° — one transform on
                  the crop box, so framer's entrance scale stays on the wrapper. */}
              <div
                className="relative h-full w-full overflow-hidden"
                style={{ transform: "scaleX(-1) rotate(3.5deg)" }}>
                <GroqImage
                  src={data.finalCtaImage}
                  alt={data.finalCtaImage?.name ?? ""}
                  quality={100}
                  style={{ width: "118.73%" }}
                  className="pointer-events-none absolute left-[-10.55%] !h-[100.24%] !w-[118.73%] max-w-none"
                />
              </div>
            </motion.div>
          </div>

          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[15.375rem] lg:bottom-[1.875rem] lg:h-[31.625rem]">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#F4F4F4]" />
            {/* GradualBlur's `strength` is the blur in rem at the strongest
                step: 1.37rem ≈ 10.9px on mobile, 1.94rem ≈ 15.5px on desktop. */}
            <div className="absolute inset-0 lg:hidden">
              <GradualBlur
                target="parent"
                position="bottom"
                height="100%"
                strength={1.37}
                divCount={6}
                curve="bezier"
                exponential
                opacity={1}
                zIndex={2}
              />
            </div>
            <div className="absolute inset-0 hidden lg:block">
              <GradualBlur
                target="parent"
                position="bottom"
                height="100%"
                strength={1.94}
                divCount={6}
                curve="bezier"
                exponential
                opacity={1}
                zIndex={2}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
